import Phaser from 'phaser';
import { coinLabel } from '../../config/coinLabel';
import { HUB_CHAPTERS, isHubLevelUnlocked, type HubChapterDef } from '../../config/hubLevels';
import { PEACEFUL_COIN_PENALTY, peacefulCoinReward } from '../../config/hub';
import type { LevelDef } from '../../core/game';
import { expRewardFor, hasBoss, HUB_MENU_TRACK } from '../../config/levels';
import { hubArenaKindFor } from '../../config/testArena';
import { LocalScoreService } from '../../services/scoreService';
import { ProgressService } from '../../services/progressService';
import { saveStorage } from '../saveStorage';
import { drawStarfield } from '../starfield';
import { spinCoin } from '../coin';
import { getAudioService } from '../audio/AudioService';
import { getMusicService } from '../audio/MusicService';
import { addMuteButton } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { installDebugReturn } from '../debugMode';
import { GOLD_HEX, POTION_GREEN, POTION_GREEN_HEX, hubProgress } from '../hubShared';
import { SETTING_TOGGLES, readSettings, writeSetting } from '../../config/gameSettings';
import { RankService } from '../../services/rankService';
import { enqueueAchievementToast } from '../../ui/achievementToast';

const ROW_W = 880;
const ROW_H = 92;
const SLOT_H = 110;
const AREA_TOP = 120;
const AREA_BOTTOM = 670;
/** Rows visible at once; the chapter has more (seven) and scrolls by whole rows. */
const VISIBLE_ROWS = Math.floor((AREA_BOTTOM - AREA_TOP) / SLOT_H); // = 5
/** Wheel-delta accumulation needed to step one row (trackpad smoothing). */
const WHEEL_STEP = 30;
/** Minimum vertical pointer travel for a swipe-scroll (also the tap cutoff). */
const SWIPE_MIN = 50;

function starString(stars: number): string {
  const n = Math.max(0, Math.min(3, stars));
  return '★'.repeat(n) + '☆'.repeat(3 - n);
}

/**
 * HubChapterScene — one hub chapter's levels, with the chapter's name as the
 * header ("THE MOON"). Rows are the episode picker's, and a tap starts the
 * level exactly as an episode would. BACK returns to The Hub's Levels tab.
 *
 * The PEACEFUL toggle (spec Revision 6) flips the whole list between two
 * separate progress tracks: Normal (red) and Peaceful (green — enemies deal
 * no damage, every level pays PEACEFUL_COIN_PENALTY fewer coins). Turning it
 * on shows a warning first. Which track is on is remembered in the save.
 *
 * Seven levels no longer fit the five 110px slots, so the list scrolls the way
 * the episode picker's Chapter 2 does: a window of VISIBLE_ROWS rows from
 * `scrollOffset`, stepped by the ▲ / ▼ arrows, the mouse wheel or a vertical
 * swipe. The first five rows keep their exact positions.
 */
export class HubChapterScene extends Phaser.Scene {
  private progress!: ProgressService;
  private scoreService!: LocalScoreService;
  private chapterNumber = 1;
  private chapter!: HubChapterDef;
  private peaceful = false;
  /** The rows (and the scroll arrows), rebuilt on every scroll step. */
  private rowsContainer?: Phaser.GameObjects.Container;
  /** Index of the first visible row. */
  private scrollOffset = 0;
  /** Accumulated wheel delta — steps one row per WHEEL_STEP. */
  private wheelAccum = 0;
  /** The peaceful warning's objects (destroyed on OK). */
  private warning: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super({ key: 'HubChapterScene' });
  }

  init(data: { chapter?: number }): void {
    this.chapterNumber = data?.chapter ?? 1;
  }

  create(): void {
    const cx = 640;
    this.progress = hubProgress();
    this.scoreService = new LocalScoreService(saveStorage());
    this.peaceful = this.progress.peacefulMode();
    this.warning = [];
    const chapter: HubChapterDef =
      HUB_CHAPTERS.find((c) => c.number === this.chapterNumber) ?? HUB_CHAPTERS[0];
    this.chapter = chapter;
    this.scrollOffset = 0;
    this.wheelAccum = 0;
    this.rowsContainer = undefined;
    const accentHex = this.peaceful ? POTION_GREEN_HEX : chapter.colorHex;

    // ── Row scrolling: mouse wheel + vertical swipe over the row column ──────
    this.input.on('wheel', (_p: Phaser.Input.Pointer, _over: unknown, _dx: number, dy: number) => {
      if (this.warning.length > 0) return;
      this.wheelAccum += dy;
      if (Math.abs(this.wheelAccum) >= WHEEL_STEP) {
        this.scrollBy(this.wheelAccum > 0 ? 1 : -1);
        this.wheelAccum = 0;
      }
    });
    this.input.on('pointerup', (p: Phaser.Input.Pointer) => {
      if (this.warning.length > 0) return;
      const dy = p.y - p.downY;
      if (Math.abs(dy) >= SWIPE_MIN && p.downY > AREA_TOP && p.downY < AREA_BOTTOM) {
        this.scrollBy(dy < 0 ? 1 : -1); // dragging up reveals later levels
      }
    });

    drawStarfield(this, 1280, 720);

    this.add
      .text(cx, 48, chapter.name.toUpperCase(), {
        fontSize: '38px',
        color: accentHex,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setName('hub-chapter-title');
    this.add
      .text(
        cx,
        84,
        `THE HUB · CHAPTER ${chapter.number.toString().padStart(2, '0')}` +
          (this.peaceful ? ' · PEACEFUL' : ''),
        { fontSize: '15px', color: this.peaceful ? POTION_GREEN_HEX : '#9aa6ad' },
      )
      .setOrigin(0.5)
      .setName('hub-chapter-subtitle');

    const backBtn = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    backBtn.on('pointerover', () => backBtn.setColor(GOLD_HEX));
    backBtn.on('pointerout', () => backBtn.setColor('#cfe0bd'));
    backBtn.on('pointerup', () => this.scene.start('HubScene', { tab: 'levels' }));

    const coinIcon = this.add.image(960, 50, 'coin').setDisplaySize(34, 34).setDepth(5);
    spinCoin(this, coinIcon);
    this.add
      .text(982, 50, coinLabel(this.progress.coins()), {
        fontSize: '22px',
        color: GOLD_HEX,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(5);

    addMuteButton(this, 1245, 50, getAudioService(this));
    addFullscreenButton(this, 1100, 50);

    // The Moon Arena — red, where the episode picker keeps its blue TEST.
    const testBtn = this.add
      .text(1180, 108, 'TEST', {
        fontSize: '18px',
        color: '#ffffff',
        fontStyle: 'bold',
        backgroundColor: chapter.testHex,
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setName('hubchapter-test')
      .setInteractive({ useHandCursor: true });
    const testHover = `#${chapter.color.toString(16).padStart(6, '0')}`;
    testBtn.on('pointerover', () => testBtn.setBackgroundColor(testHover));
    testBtn.on('pointerout', () => testBtn.setBackgroundColor(chapter.testHex));
    testBtn.on('pointerup', () => {
      const levelId = chapter.arenaId;
      this.scene.start('GameScene', { levelId });
      this.scene.launch('HudScene', { levelId, arena: hubArenaKindFor(levelId) });
    });

    // The shelf is reachable from here too, since the Diamond lands on it.
    const storageBtn = this.add
      .text(1180, 162, 'STORAGE', {
        fontSize: '15px',
        color: '#e8ecef',
        fontStyle: 'bold',
        backgroundColor: '#4a4f57',
        padding: { x: 10, y: 10 },
      })
      .setOrigin(0.5)
      .setName('hubchapter-storage')
      .setInteractive({ useHandCursor: true });
    storageBtn.on('pointerover', () => storageBtn.setBackgroundColor('#666c76'));
    storageBtn.on('pointerout', () => storageBtn.setBackgroundColor('#4a4f57'));
    storageBtn.on('pointerup', () => this.scene.start('StorageScene', { from: 'HubChapterScene' }));

    // PEACEFUL — the toggle between the two tracks. Green when on.
    const peaceBg = this.peaceful ? '#1a6b1a' : '#4a4f57';
    const peaceBtn = this.add
      .text(1180, 226, this.peaceful ? 'PEACEFUL: ON' : 'PEACEFUL: OFF', {
        fontSize: '13px',
        color: '#ffffff',
        fontStyle: 'bold',
        backgroundColor: peaceBg,
        padding: { x: 8, y: 10 },
      })
      .setOrigin(0.5)
      .setName('hubchapter-peaceful')
      .setInteractive({ useHandCursor: true });
    peaceBtn.on('pointerover', () =>
      peaceBtn.setBackgroundColor(this.peaceful ? '#2a8a2a' : '#666c76'),
    );
    peaceBtn.on('pointerout', () => peaceBtn.setBackgroundColor(peaceBg));
    peaceBtn.on('pointerup', () => this.togglePeaceful());
    this.add
      .text(1180, 262, this.peaceful ? `−${PEACEFUL_COIN_PENALTY} coins` : 'no damage', {
        fontSize: '11px',
        color: this.peaceful ? POTION_GREEN_HEX : '#7a818c',
        fontStyle: 'italic',
      })
      .setOrigin(0.5);

    // SETTINGS — the grey gear under PEACEFUL (spec 2026-10-27 §3).
    const gear = this.textures.exists('ui-gear')
      ? this.add.image(1180, 302, 'ui-gear').setDisplaySize(40, 40)
      : this.add.text(1180, 302, '⚙', { fontSize: '34px', color: '#9aa0a8' }).setOrigin(0.5);
    gear.setName('hubchapter-settings').setInteractive({ useHandCursor: true });
    gear.on('pointerover', () => gear.setAlpha(0.75));
    gear.on('pointerout', () => gear.setAlpha(1));
    gear.on('pointerup', () => {
      if (this.warning.length === 0) this.showSettings();
    });

    const music = getMusicService(this);
    music.playTrack(HUB_MENU_TRACK);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => music.stop());

    this.renderRows();

    installDebugReturn(this);
  }

  /**
   * Draw the visible window of rows into a fresh container (replacing any
   * prior one), plus the scroll arrows when the chapter overflows.
   */
  private renderRows(): void {
    this.rowsContainer?.destroy();
    const container = this.add.container(0, 0).setDepth(1);
    this.rowsContainer = container;
    const cx = 640;
    const levels = this.chapter.levels;
    const maxOffset = Math.max(0, levels.length - VISIBLE_ROWS);
    this.scrollOffset = Phaser.Math.Clamp(this.scrollOffset, 0, maxOffset);
    const visible = levels.slice(this.scrollOffset, this.scrollOffset + VISIBLE_ROWS);
    visible.forEach((level, i) => {
      const rowY = AREA_TOP + SLOT_H * i + SLOT_H / 2;
      this.makeLevelRow(cx, rowY, level, container);
    });
    if (maxOffset > 0) this.buildScrollUi(container, levels.length, maxOffset);
  }

  /** Step the row window by `step` rows and redraw. */
  private scrollBy(step: number): void {
    const maxOffset = Math.max(0, this.chapter.levels.length - VISIBLE_ROWS);
    const next = Phaser.Math.Clamp(this.scrollOffset + step, 0, maxOffset);
    if (next === this.scrollOffset) return;
    this.scrollOffset = next;
    this.renderRows();
  }

  /**
   * ▲ / ▼ step buttons plus an "a–b / n" range readout in the right margin,
   * at the episode picker's spots (1140, 349 / 441) — under the PEACEFUL
   * toggle, clear of the HUD-free column.
   */
  private buildScrollUi(
    container: Phaser.GameObjects.Container,
    totalRows: number,
    maxOffset: number,
  ): void {
    const x = 1140;
    const midY = (AREA_TOP + AREA_BOTTOM) / 2;
    const accentHex = this.peaceful ? POTION_GREEN_HEX : this.chapter.colorHex;
    const makeArrow = (y: number, glyph: string, step: number, enabled: boolean, name: string) => {
      const btn = this.add
        .text(x, y, glyph, {
          fontSize: '22px',
          color: enabled ? accentHex : '#555a66',
          backgroundColor: '#15131f',
          padding: { x: 14, y: 10 },
        })
        .setOrigin(0.5)
        .setDepth(2)
        .setName(name);
      container.add(btn);
      if (enabled) {
        btn.setInteractive({ useHandCursor: true });
        btn.on('pointerover', () => btn.setBackgroundColor('#262234'));
        btn.on('pointerout', () => btn.setBackgroundColor('#15131f'));
        btn.on('pointerup', (p: Phaser.Input.Pointer) => {
          if (p.getDistance() < SWIPE_MIN) this.scrollBy(step); // swipes step via the scene handler
        });
      }
    };
    makeArrow(midY - 46, '▲', -1, this.scrollOffset > 0, 'hub-scroll-up');
    makeArrow(midY + 46, '▼', +1, this.scrollOffset < maxOffset, 'hub-scroll-down');
    const first = this.scrollOffset + 1;
    const last = Math.min(this.scrollOffset + VISIBLE_ROWS, totalRows);
    container.add(
      this.add
        .text(x, midY, `${first}–${last} / ${totalRows}`, { fontSize: '13px', color: '#9aa6ad' })
        .setOrigin(0.5)
        .setDepth(2)
        .setName('hub-scroll-range'),
    );
  }

  /**
   * Flip the track. Off → on shows the warning first (the penalty and the
   * separate progress); on → off just redraws. Either way the choice is
   * saved before the redraw, so a level started next runs on the new track.
   */
  private togglePeaceful(): void {
    if (this.warning.length > 0) return;
    const next = !this.peaceful;
    this.progress.setPeacefulMode(next);
    if (!next) {
      this.scene.restart({ chapter: this.chapterNumber });
      return;
    }
    this.showPeacefulWarning();
  }

  /**
   * The Settings panel: one row per SETTING_TOGGLES entry, each an ON / OFF
   * switch saved the moment it is pressed (device-only, `hambi.settings`).
   * It shares `warning` with the Peaceful notice, so swipes, the wheel and
   * the toggles underneath are all held while it is open.
   */
  private showSettings(): void {
    const W = 1280;
    const H = 720;
    const D = 100;
    const keep = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      this.warning.push(o);
      return o;
    };
    const close = () => {
      this.warning.forEach((o) => o.destroy());
      this.warning = [];
    };
    keep(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0.7)
        .setInteractive()
        .setDepth(D),
    );
    const rowsH = SETTING_TOGGLES.length * 84;
    const panelH = 190 + rowsH;
    const top = H / 2 - panelH / 2;
    keep(
      this.add
        .rectangle(W / 2, H / 2, 640, panelH, 0x1b1d22, 1)
        .setStrokeStyle(2, 0x8a9099)
        .setDepth(D + 1)
        .setName('settings-panel'),
    );
    keep(
      this.add
        .text(W / 2, top + 44, 'SETTINGS', {
          fontSize: '30px',
          color: '#d6dbe0',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    SETTING_TOGGLES.forEach((t, i) => {
      const y = top + 112 + i * 84;
      keep(
        this.add
          .text(W / 2 - 280, y - 12, t.label, {
            fontSize: '20px',
            color: '#ffffff',
            fontStyle: 'bold',
          })
          .setOrigin(0, 0.5)
          .setDepth(D + 2),
      );
      keep(
        this.add
          .text(W / 2 - 280, y + 16, t.hint, {
            fontSize: '13px',
            color: '#9aa6ad',
            fontStyle: 'italic',
          })
          .setOrigin(0, 0.5)
          .setDepth(D + 2),
      );
      const btn = keep(
        this.add
          .text(W / 2 + 230, y, '', {
            fontSize: '16px',
            color: '#ffffff',
            fontStyle: 'bold',
            padding: { x: 12, y: 8 },
          })
          .setOrigin(0.5)
          .setDepth(D + 2)
          .setName(`settings-toggle-${t.key}`)
          .setInteractive({ useHandCursor: true }),
      );
      const paint = () => {
        const on = readSettings()[t.key];
        btn.setText(on ? 'ON' : 'OFF').setBackgroundColor(on ? '#1a6b1a' : '#4a4f57');
      };
      paint();
      btn.on('pointerup', () => {
        writeSetting(t.key, !readSettings()[t.key]);
        paint();
        // Gearbox (bronze, spec 2026-10-29): flipping ANY toggle, either way.
        try {
          const a = new RankService(saveStorage()).award('gearbox');
          if (a) enqueueAchievementToast({ title: a.name, body: a.how, iconKey: a.icon });
        } catch (err) {
          console.warn('[HubChapterScene] gearbox award failed', err);
        }
      });
    });
    const closeBg = keep(
      this.add
        .rectangle(W / 2, top + panelH - 48, 200, 48, 0x4a4f57)
        .setStrokeStyle(2, 0x8a9099)
        .setInteractive({ useHandCursor: true })
        .setDepth(D + 2)
        .setName('settings-close'),
    );
    keep(
      this.add
        .text(W / 2, top + panelH - 48, 'CLOSE', {
          fontSize: '18px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    closeBg.on('pointerup', close);
  }

  /** The warning shown when Peaceful is switched on; OK redraws the list. */
  private showPeacefulWarning(): void {
    const W = 1280;
    const H = 720;
    const D = 100;
    const keep = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      this.warning.push(o);
      return o;
    };
    keep(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0.7)
        .setInteractive()
        .setDepth(D),
    );
    keep(
      this.add
        .rectangle(W / 2, H / 2, 720, 300, 0x0f1f12, 1)
        .setStrokeStyle(2, POTION_GREEN)
        .setDepth(D + 1)
        .setName('peaceful-warning'),
    );
    keep(
      this.add
        .text(W / 2, H / 2 - 104, 'PEACEFUL MODE', {
          fontSize: '30px',
          color: POTION_GREEN_HEX,
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    keep(
      this.add
        .text(
          W / 2,
          H / 2 - 22,
          'Enemies are passive and deal no damage.\n' +
            `WARNING: every level here pays ${PEACEFUL_COIN_PENALTY} FEWER coins.\n` +
            'Peaceful progress is tracked separately, from Level I.\n' +
            'Your Normal progress is kept for when you switch back.',
          {
            fontSize: '17px',
            color: '#e8ecef',
            align: 'center',
            lineSpacing: 6,
          },
        )
        .setOrigin(0.5)
        .setDepth(D + 2)
        .setName('peaceful-warning-text'),
    );
    const okBg = keep(
      this.add
        .rectangle(W / 2, H / 2 + 104, 200, 52, 0x1a6b1a)
        .setStrokeStyle(2, 0x4caf50)
        .setInteractive({ useHandCursor: true })
        .setDepth(D + 2)
        .setName('peaceful-warning-ok'),
    );
    keep(
      this.add
        .text(W / 2, H / 2 + 104, 'OK', { fontSize: '20px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    okBg.on('pointerup', () => {
      this.warning.forEach((o) => o.destroy());
      this.warning = [];
      this.scene.restart({ chapter: this.chapterNumber });
    });
  }

  private makeLevelRow(
    cx: number,
    rowY: number,
    level: LevelDef,
    container: Phaser.GameObjects.Container,
  ): void {
    const left = cx - ROW_W / 2;
    const top = rowY - ROW_H / 2;
    const right = cx + ROW_W / 2;
    const peaceful = this.peaceful;
    const unlocked = isHubLevelUnlocked(level.id, this.progress.hubCompleted(peaceful));
    const stars = this.progress.hubStars(level.id, peaceful);
    const trackColor = peaceful ? POTION_GREEN : this.chapter.color;
    const trackHex = peaceful ? POTION_GREEN_HEX : this.chapter.colorHex;
    const accentNum = unlocked ? trackColor : 0x555a66;
    const accentHex = unlocked ? trackHex : '#7a818c';

    const panel = this.add.graphics();
    container.add(panel);
    const drawPanel = (hover: boolean) => {
      panel.clear();
      panel.fillStyle(0x15131f, unlocked ? (hover ? 0.95 : 0.8) : 0.6);
      panel.fillRoundedRect(left, top, ROW_W, ROW_H, 12);
      const lit = hover && unlocked;
      panel.lineStyle(lit ? 2 : 1, accentNum, lit ? 1 : 0.5);
      panel.strokeRoundedRect(left, top, ROW_W, ROW_H, 12);
      panel.fillStyle(accentNum, 1);
      panel.fillRoundedRect(left, top, 6, ROW_H, { tl: 12, bl: 12, tr: 0, br: 0 });
    };
    drawPanel(false);

    const bgKey =
      level.background && this.textures.exists(level.background) ? level.background : '';
    if (bgKey) {
      const wash = this.add
        .image(cx, rowY, bgKey)
        .setDepth(0.5)
        .setAlpha(unlocked ? 0.38 : 0.15);
      const s = Math.max(ROW_W / wash.width, ROW_H / wash.height);
      wash.setScale(s);
      const maskG = this.make.graphics();
      maskG.fillStyle(0xffffff);
      maskG.fillRoundedRect(left, top, ROW_W, ROW_H, 12);
      wash.setMask(maskG.createGeometryMask());
      wash.once('destroy', () => maskG.destroy());
      container.add(wash);
    }

    const textX = left + 30;
    // "LEVEL V · BOSS" on the chapter's boss level (The Space Station).
    const boss = hasBoss(level);
    container.add(
      this.add
        .text(textX, rowY - 26, `LEVEL ${level.episode ?? level.id}${boss ? '  ·  BOSS' : ''}`, {
          fontSize: '13px',
          color: accentHex,
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setDepth(1)
        .setName(`hub-level-label-${level.id}`),
    );
    container.add(
      this.add
        .text(textX, rowY + 2, level.name, {
          fontSize: '27px',
          color: unlocked ? '#ffffff' : '#7a818c',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setDepth(1)
        .setName(`hub-level-${level.id}`),
    );
    const subtitle = unlocked
      ? `${level.waves.length} waves · towers have ${level.towerHp ?? 3} HP` +
        (peaceful ? ' · peaceful' : '')
      : 'Beat the previous level to unlock';
    container.add(
      this.add
        .text(textX, rowY + 28, subtitle, {
          fontSize: '14px',
          color: unlocked ? '#9aa6ad' : '#c08a3e',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0.5)
        .setDepth(1),
    );

    container.add(
      this.add
        .text(right - 24, rowY - 24, starString(stars), {
          fontSize: '18px',
          color: unlocked ? GOLD_HEX : '#555a66',
        })
        .setOrigin(1, 0.5)
        .setDepth(1),
    );
    // Best score is a Normal-track number: Peaceful runs are not scored.
    const scoreText = this.add
      .text(right - 24, rowY + 22, peaceful ? 'PEACEFUL' : 'BEST: —', {
        fontSize: '16px',
        color: peaceful ? POTION_GREEN_HEX : '#9aa6ad',
      })
      .setOrigin(1, 0.5)
      .setDepth(1);
    container.add(scoreText);
    if (!peaceful) {
      void this.scoreService
        .best(level.id)
        .then((best) => scoreText.setText(best > 0 ? `BEST: ${best}` : 'BEST: —'))
        .catch(() => {});
    }

    // Coins then "+N EXP" beside them, all left of the LOCKED marker (right-150).
    const rewardX = right - 420;
    const coinIcon = this.add.image(rewardX, rowY, 'coin').setDisplaySize(28, 28).setDepth(1);
    if (!unlocked) coinIcon.setAlpha(0.5);
    container.add(coinIcon);
    const reward = peaceful ? peacefulCoinReward(level.coinReward) : level.coinReward;
    container.add(
      this.add
        .text(rewardX + 18, rowY, `+${reward}`, {
          fontSize: '18px',
          color: unlocked ? GOLD_HEX : '#7a818c',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setDepth(1)
        .setName(`hub-level-coins-${level.id}`),
    );
    // Rank exp is a Normal-track reward too.
    const exp = peaceful ? 0 : expRewardFor(level);
    if (exp > 0) {
      container.add(
        this.add
          .text(rewardX + 96, rowY, `+${exp} EXP`, {
            fontSize: '15px',
            color: unlocked ? '#9fd8ff' : '#7a818c',
            fontStyle: 'bold',
          })
          .setOrigin(0, 0.5)
          .setDepth(1),
      );
    }

    if (!unlocked) {
      container.add(
        this.add
          .text(right - 150, rowY, 'LOCKED', {
            fontSize: '20px',
            color: '#6a7078',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setDepth(1),
      );
    }

    const hit = this.add.zone(cx, rowY, ROW_W, ROW_H).setOrigin(0.5);
    container.add(hit);
    if (unlocked) {
      hit.setInteractive({ useHandCursor: true });
      hit.on('pointerover', () => drawPanel(true));
      hit.on('pointerout', () => drawPanel(false));
      hit.on('pointerup', (p: Phaser.Input.Pointer) => {
        if (this.warning.length > 0) return;
        if (p.getDistance() >= SWIPE_MIN) return; // a swipe-scroll, not a tap
        this.scene.start('GameScene', { levelId: level.id, peaceful });
        this.scene.launch('HudScene', { levelId: level.id, peaceful });
      });
    }
  }
}
